import React, { useState, useEffect, useRef, } from 'react';
import { useTheme, styled, } from '@mui/material/styles';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import jsAPI from '../helpers/jsAPI';
import LoadingButton from '@mui/lab/LoadingButton';
import UserContext from '../context/UserContext';
import LinearProgress from '@mui/material/LinearProgress';
import AppConfig from '../config/app.json';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import PrivacyPolicyBanner from '../layouts/components/PrivacyPolicyBanner';
import qs from 'qs';

const secure = require('../helpers/secure.js');

const styles = (theme) => ({
  root: {
    backgroundColor: "primary.light",
    height: "100vh",
    overflow: "hidden"
  },
  primaryText: {
    color: "primary.light",
    lineHeight: "normal",
    fontSize: 110
  },
  loginContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  loginContent: {
    padding: "20vh 1.875rem 0.3125rem 1.875rem",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      padding: "2.5rem 1.25rem 1.25rem 1.25rem",
    },
  },
  secondaryContainer: {
    [theme.breakpoints.down("md")]: {
      display: "none"
    },
  },
  displayImage: {
    [theme.breakpoints.down("md")]: {
      backgroundImage: 'url("/images/login.svg")'
    },
  },
  formContent: {
    marginTop: "0.625rem",
    "& > div": {
      marginBottom: "0.625rem",
    },
    width: "100%",
  },
  jobs: {
    padding: "0 0.3125rem 0 0.3125rem",
    margin: 0,
    border: "0.0625rem solid",
    borderColor: "secondary.main",
    marginLeft: "0.3125rem",
    borderRadius: "0.25rem",
  },
});

const Form = styled('form')``;

const LoginForm = () => {
  const theme = useTheme();
  const stylesSX = styles(theme);
  const textRef = useRef(null);
  const history = useHistory();
  const routerParams = useParams();
  const routerLocation = useLocation();
  const [showClientCode, setShowClientCode] = useState(false);
  const [clientCode, setClientCode] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [user, setUser] = React.useContext(UserContext);
  
  const handleLogin = async (event) => {
    event.preventDefault();

    setIsLoggingIn(true);
    setErrorMessage("");

    const queryString = qs.parse(routerLocation.search, { ignoreQueryPrefix: true });
    const { redirect_uri, sso, } = queryString;

    try {
      const responseJSON = await jsAPI.request("/api/login/authenticate", {
        method: "POST",
        body: JSON.stringify({
          client_code: clientCode,
          email_address: emailAddress,
          password: secure.aes256Encrypt(password),
          sso,
        })
      });
  
      if (responseJSON.success === true) {
        if (responseJSON.sso?.toLowerCase() === "ess") {
          const responseJSON2 = await jsAPI.request("/api/time_keeping/ess", {
            method: "POST"
          });
  
          if (responseJSON2.success === false) {
            throw new Error(responseJSON2.error_message);
          }
  
          window.location.href = responseJSON2.url + `?token=${responseJSON2.token}&account_code=${responseJSON2.account_code}`;
          return;
        }
        else if (responseJSON.sso?.toLowerCase() === "pph") {
          const responseJSON2 = await jsAPI.request("/api/payroll/sso", {
            method: "POST"
          });
  
          if (responseJSON2.success === false) {
            throw new Error(responseJSON2.error_message);
          }
  
          window.location.href = responseJSON2.url + `?token=${responseJSON2.token}&account_code=${responseJSON2.account_code}`;
          return;
        }
  
        setUser({...user, isLoggedIn: true});
        if (redirect_uri) {
          history.push(redirect_uri);
        }
        else {
          history.push(`/${responseJSON.client_code}/app`);
        }
      }
      else {
        throw new Error(responseJSON.error_message);
      }
    }
    catch (err) {
      setErrorMessage(err.message);
      setClientCode(routerParams.client_code || "");
      setEmailAddress("");
      setPassword("");
      if (textRef.current) {
        textRef.current.focus();
      }
    }
    finally {
      setIsLoggingIn(false);
    }
  }

  useEffect(() => {
    document.title = `${AppConfig.product_name} - LOGIN`;
    const checkSession = async () => {
      setLoading(true);
      const responseJSON = await jsAPI.request("/api/login/check_session");
      const isLoggedIn = responseJSON.success;
      const client_code = responseJSON.client_code

      setUser(val => {
        return {...val, isLoggedIn}
      });
      if (isLoggedIn) {
        const queryString = qs.parse(routerLocation.search, { ignoreQueryPrefix: true });

        if (queryString?.redirect_uri) {
          const { redirect_uri } = queryString;
        
          // check if client and url is tickets, if yes add /client
          if (responseJSON.is_client === true ) {
            const newUrl = redirect_uri;
            history.push(newUrl);
          }
          // check if support and url is tickets, if yes remove /client 
          else if (responseJSON.is_client === false) {
            const newUrl = redirect_uri.replace("/client", "");
            history.push(newUrl);
          } else {
            history.push(redirect_uri);
          }
        } else {
          const clientCode =  client_code ;
          const url = `/${clientCode}/app`
          history.push(url);
        }
      }

      setLoading(false);
    }
    checkSession();
  }, [history, setUser, routerLocation.search]);

  useEffect(() => {
    if (routerParams.client_code) {
      setShowClientCode(false);
    }
    else {
      setShowClientCode(true);
    }

    setClientCode(routerParams.client_code || "");
  }, [routerParams]);

  useEffect(() => {
    if (loading === false) {
      if (textRef.current) {
        textRef.current.focus();
      }
    }
  }, [loading]);

  return (
    <Box sx={stylesSX.root}>
      {loading === true ? (
        <LinearProgress />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <Grid container style={{ display: "flex", flex: 1 }}>
              <Grid item lg={4} md={4} sm={12} xs={12} style={{ display: "flex" }}>
                <Paper sx={stylesSX.loginContainer}>
                  <div style={{display: "inline-flex", alignItems: "center", padding: 15}}>
                    <img src={AppConfig.logo_path} style={{maxHeight: 40}} alt="logo" />
                    <Typography variant="h3" color="primary.dark">
                      {`${AppConfig.product_name}`}
                    </Typography>
                    {/* <Box className={classes.jobs}>
                      <Typography sx={{fontSize: 10, padding: 0, margin: 0, color: "secondary.main", fontWeight: "bold",}}>JOBS</Typography>
                    </Box> */}
                  </div>
                  <Box sx={stylesSX.loginContent}>
                    <Typography variant="h3" color="primary.dark" style={{marginTop: 8}}>
                      User Login
                    </Typography>
                    <Form sx={stylesSX.formContent} noValidate onSubmit={handleLogin}>
                      {showClientCode === true && (
                        <TextField
                          inputRef={textRef}
                          variant="outlined"
                          size="small"
                          label="Client Code"
                          fullWidth
                          value={clientCode}
                          onChange={(event) => setClientCode(event.target.value)}
                        />
                      )}
                      <TextField
                        inputRef={el => {
                          if (showClientCode === false) {
                            textRef.current = el;
                          }
                        }}
                        variant="outlined"
                        size="small"
                        label="Email Address"
                        fullWidth
                        type="email"
                        value={emailAddress}
                        onChange={(event) => setEmailAddress(event.target.value)}
                      />
                      <TextField
                        variant="outlined"
                        size="small"
                        label="Password"
                        fullWidth
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                      {errorMessage && (
                        <div style={{display: "flex", justifyContent: "center", flex: 1}}>
                          <Typography style={{color: "red"}}>{errorMessage}</Typography>
                        </div>
                      )}
                      <LoadingButton
                        color="primary"
                        variant="contained"
                        fullWidth
                        type="submit"
                        loading={isLoggingIn}
                        loadingIndicator="Please wait..."
                      >
                        LOGIN
                      </LoadingButton>
                    </Form>
                    <div style={{ display: "flex" }}>
                      <Button
                        color="secondary"
                        style={{ width: 161 }}
                        onClick={() => {
                          let link = "/accounts/forgot_password";
                          if (routerParams.client_code) {
                            link = `/${routerParams.client_code}/accounts/forgot_password`;
                          }
                          history.push(link);
                        }}
                      >
                        Forgot Password?
                      </Button>
                      <div style={{ flex: 1 }}></div>
                      <Button
                        color="secondary"
                        style={{ width: 150 }}
                        onClick={() => {
                          let link = "/accounts/signup";
                          if (routerParams.client_code) {
                            link = `/${routerParams.client_code}/accounts/signup`;
                          }
                          history.push(link);
                        }}
                      >
                        Employee Signup
                      </Button>
                    </div>
                    <Box
                      sx={{ ...stylesSX.displayImage, flex: 1, display: "flex", backgroundRepeat: "no-repeat", backgroundSize: "contain", backgroundPosition: "center",}}
                    />
                    <Link
                      href="https://jeonsoft.com"
                      underline="hover"
                      target="_blank"
                      rel="noopener"
                      variant="body1"
                      color="text.secondary"
                    >
                      Powered by JeonSoft Corporation
                    </Link>
                  </Box>
                </Paper>
              </Grid>
              <Grid item lg={8} md={8}
                style={{backgroundColor: "#194944", overflow: "hidden !important"}}
                sx={stylesSX.secondaryContainer}
              >
                <div style={{zIndex: 999, position: "absolute", color: "white", margin: "25vh 3.125rem"}}>
                  <Typography color="white" variant="h1">TODAY IS A <br/> GOOD DAY TO</Typography>
                  <Typography variant="h1" sx={stylesSX.primaryText}>HIRE GREAT TALENTS</Typography>
                  <Typography color="white" variant="h5" style={{fontWeight: "lighter"}}>WELCOME TO JEONSOFT WEB HIRING PORTAL <br/> WE ARE EXCITED HELP YOU GROW. <br/>CLICK LOGIN TO START HIRING.</Typography>
                </div>
                {/* <video autoPlay muted loop style={{
                  right: 0,
                  bottom: 0,
                  width: "auto",
                  height: "100vh",
                  mixBlendMode: "multiply",
                  opacity: "50%"
                }}>
                  <source src="/images/login_bck.mp4" type="video/mp4" />
                </video> */}
              </Grid>
            </Grid>
            <PrivacyPolicyBanner/>
        </div>
      )}
    </Box>
  );
}


export default LoginForm;
